<template>
  <ion-app class="relative">
    <AppOfflineBanner />
    <AppModalsContainer />
    <CardContextOverlay v-if="featureFlags.cardContext" />

    <ion-router-outlet :animated="true" :swipe-gesture="true" />

    <!-- Custom Splash Screen Spinner Overlay (iOS only) -->
    <div
      v-if="showCustomSpinner"
      class="fixed inset-0 z-[9999] transition-opacity duration-300"
      :class="{ 'opacity-0': !showCustomSpinner }"
      :style="{
        backgroundImage: 'url(/backgrounds/spectra-default-background-01.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }">
      <!-- Spinner positioned in lower third -->
      <div
        class="flex absolute right-0 bottom-0 left-0 flex-col justify-center items-center pb-20">
        <div class="flex flex-col items-center space-y-4">
          <!-- FontAwesome Circle Notch Spinner -->
          <fa-icon
            icon="circle-notch"
            size="3x"
            class="text-white animate-spin" />
          <p class="text-sm font-medium text-white opacity-90 drop-shadow-lg">
            Loading your circle...
          </p>
        </div>
      </div>
    </div>
  </ion-app>
</template>

<script setup>
import {
  ref,
  computed,
  provide,
  watch,
  inject,
  onMounted,
  onUnmounted,
  defineAsyncComponent
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { IonApp } from '@ionic/vue'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreMessages } from '@/stores/storeMessages'
import { useStoreNotifications } from '@/stores/storeNotifications'
import { useSplashScreen } from '@/composables/useSplashScreen'
import { usePushNotifications } from '@/composables/usePushNotifications'

import AppModalsContainer from '@/components/AppModalsContainer'
import AppOfflineBanner from '@/components/AppOfflineBanner.vue'

// Lazy-load CardContextOverlay to avoid Pinia initialization before app.use(pinia)
const CardContextOverlay = defineAsyncComponent(
  () => import('@/components/CardContextNavigator/CardContextOverlay.vue')
)

const storeProfile = useStoreProfile()
const storeAuth = useStoreAuth()
const storeMessages = useStoreMessages()
const { initSplashScreen, showCustomSpinner } = useSplashScreen()
const { initPushNotifications, unregisterPush } = usePushNotifications()

let unsubscribeMessages = null

const startMessages = () => {
  if (unsubscribeMessages) return
  unsubscribeMessages = storeMessages.subscribeToMessages()
  storeMessages.fetchUnreadCount()
  useStoreNotifications().init()
}

const stopMessages = () => {
  unsubscribeMessages?.()
  unsubscribeMessages = null
  storeMessages.reset()
  useStoreNotifications().unsubscribeFromRealtime()
}

// Update notification state
const updateAvailable = ref(false)

// Event handlers for Capgo updates
const handleUpdateAvailable = (event) => {
  console.log('Update available event received:', event.detail)
  updateAvailable.value = true
}

const handleNoUpdate = (event) => {
  console.log('No update needed event received:', event.detail)
  updateAvailable.value = false
}

const applyUpdate = () => {
  if (window.applyCapgoUpdate) {
    window.applyCapgoUpdate()
  } else {
    console.error('Capgo update function not available')
  }
}

// Handle app resume from background (iOS/native)
const isRefreshing = ref(false)
const handleAppResumeFromBackground = async () => {
  // Prevent multiple simultaneous refreshes
  if (isRefreshing.value) {
    console.log('[App] Already refreshing, skipping')
    return
  }

  // Only refresh if user was previously authenticated
  if (!storeAuth.user) {
    console.log('[App] No user authenticated, skipping resume refresh')
    return
  }

  isRefreshing.value = true
  console.log('[App] Handling app resume from background')

  try {
    await storeAuth.refreshAfterResume()
  } catch (error) {
    console.error('[App] Error refreshing after resume:', error)
  } finally {
    isRefreshing.value = false
  }
}

// Watch for user authentication to initialize push notifications
watch(
  () => storeAuth.user,
  (newUser, oldUser) => {
    if (newUser && !oldUser) {
      // User just logged in - initialize push
      console.log('[App] User authenticated, initializing push notifications')
      initPushNotifications()
      startMessages()
    } else if (!newUser && oldUser) {
      // User logged out - unregister push
      console.log('[App] User logged out, unregistering push notifications')
      unregisterPush()
      stopMessages()
    }
  }
)

onMounted(() => {
  storeAuth.init()

  // Initialize splash screen management for iOS
  initSplashScreen()

  // If user is already authenticated (e.g., from persisted session), init push
  if (storeAuth.user) {
    initPushNotifications()
    startMessages()
  }

  // Listen for Capgo update events from main.js
  window.addEventListener('capgo-update-available', handleUpdateAvailable)
  window.addEventListener('capgo-no-update', handleNoUpdate)

  // Listen for app resume from background (iOS/native)
  window.addEventListener(
    'app-resumed-from-background',
    handleAppResumeFromBackground
  )
})

onUnmounted(() => {
  // Clean up event listeners
  window.removeEventListener('capgo-update-available', handleUpdateAvailable)
  window.removeEventListener('capgo-no-update', handleNoUpdate)
  window.removeEventListener(
    'app-resumed-from-background',
    handleAppResumeFromBackground
  )
  unsubscribeMessages?.()
  unsubscribeMessages = null
})

const isIOS = computed(() => {
  const htmlClassList = document.documentElement.classList
  return htmlClassList.contains('plt-ios')
})

const isMobileApp = computed(() => {
  const htmlClassList = document.documentElement.classList
  const isTabletOrMobile =
    htmlClassList.contains('plt-tablet') || htmlClassList.contains('plt-mobile')
  return isTabletOrMobile && isIOS.value
})

const isNativeApp = computed(() => {
  return Capacitor.getPlatform() !== 'web'
})

provide('platform', {
  isNativeApp,
  isMobileApp,
  isIOS
})

// Feature flags - read from environment variables at build time
const featureFlags = {
  cardContext: import.meta.env.VITE_FEATURE_CARD_CONTEXT === 'true',
  watchLog: import.meta.env.VITE_FEATURE_WATCH_LOG === 'true'
}
provide('featureFlags', featureFlags)

const userHasAccess = computed(() => {
  // return true
  return storeProfile.currentUserHasAccess
})

// // Other reactive references and functions
const route = useRoute()
const router = useRouter()

const searchQuery = ref('')
const showSearchResults = ref(false)
const isOnSearchPage = computed(() => route.name?.includes('Search'))
const closeSearchResults = () => {
  showSearchResults.value = false
}
watch(isOnSearchPage, (newVal) => {
  if (!newVal) {
    showSearchResults.value = false
  }
})
const mobileSideBarOpen = ref(false)
function toggleMobileSidebar() {
  mobileSideBarOpen.value = !mobileSideBarOpen.value
}
function closeMobileSidebar() {
  mobileSideBarOpen.value = false
}

// provide('mobileSidebar', {
//   mobileSideBarOpen,
//   toggleMobileSidebar,
//   closeMobileSidebar
// })

// router.beforeEach(() => {
//   if (mobileSideBarOpen.value) {
//     closeMobileSidebar()
//   }
// })

// const handleSearchQuery = (query) => {
//   searchQuery.value = query
//   showSearchResults.value = true

//   if (isOnSearchPage.value) {
//     showSearchResults.value = false
//     router.replace({
//       name: 'WebSearch',
//       query: {
//         ...router.currentRoute.value.query,
//         search: query
//       }
//     })
//   }
// }
</script>
<style scoped>
/* .ios .custom-offset-bottom {
  --offset-bottom: -63px !important;
} */
</style>
